import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiMessageCircle, FiUsers, FiWifi, FiWifiOff } from 'react-icons/fi';
import Card from '../../shared/components/Card/Card';
import Input from '../../shared/components/Input/Input';
import Button from '../../shared/components/Button/Button';
import Avatar from '../../shared/components/Avatar/Avatar';
import ChatHeader from './components/ChatHeader';
import MessageList from './components/MessageList';
import MessageInput from './components/MessageInput';
import useChat from './hooks/useChat';
import styles from './ChatRoom.module.css';

export default function ChatRoom() {
  const { roomId } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [copied, setCopied] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > 768);

  const { messages, joined, setJoined, joinRoom, leaveRoom, sendMessage, connected, users } = useChat(
    roomId,
    name
  );

  const currentUserObj = useMemo(() => {
    return users.find((u) => u.name === name);
  }, [users, name]);

  const myAvatarId = currentUserObj ? currentUserObj.avatarId : undefined;

  const otherUsers = useMemo(() => {
    return users.filter((u) => u.name !== name);
  }, [users, name]);

  useEffect(() => {
    const savedName = localStorage.getItem('username');
    if (savedName && !joined) {
      setName(savedName);
      localStorage.setItem('roomId', roomId);
      joinRoom(savedName);
      setJoined(true);
    }
  }, [roomId, joined, joinRoom, setJoined]);

  const handleJoinRoom = () => {
    if (!name.trim()) return;
    localStorage.setItem('username', name);
    localStorage.setItem('roomId', roomId);
    joinRoom(name);
  };

  const handleSendMessage = () => {
    if (text.trim()) {
      sendMessage(name, text);
      setText('');
    }
  };

  const handleLeaveRoom = () => {
    leaveRoom(name);
    localStorage.removeItem('username');
    localStorage.removeItem('roomId');
    navigate('/');
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/room/${roomId}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!joined) {
    return (
      <div className={styles.joinPage}>
        <div className={styles.joinContainer}>
          <Card variant="elevated" padding="large" className={styles.joinCard}>
            <div className={styles.logoWrapper}>
              <FiMessageCircle className={styles.logo} />
            </div>
            <h2 className={styles.joinTitle}>Join Chat Room</h2>
            <h5 className={styles.joinRoomId}>{roomId}</h5>
            <p className={styles.joinSubtitle}>Enter your details to continue</p>
            <div className={styles.formGroup}>
              <Input
                type="text"
                placeholder="Enter your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleJoinRoom()}
              />
            </div>
            <Button onClick={handleJoinRoom} size="large" className={styles.joinBtn}>
              Join Room
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.layout}>
      <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''}`}>
        <div className={styles.sidebarHeader}>
          <FiUsers size={18} />
          <span>Participants</span>
        </div>

        <div className={styles.sidebarSection}>
          <div className={styles.sidebarLabel}>You</div>
          <div className={styles.participantItem}>
            <Avatar name={name} avatarId={myAvatarId} size={34} />
            <div className={styles.participantInfo}>
              <span className={styles.participantName}>{name}</span>
              <span className={styles.participantStatus}>
                <span className={connected ? styles.statusOnline : styles.statusOffline} />
                {connected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.sidebarSection}>
          <div className={styles.sidebarLabel}>
            In Room
            <span className={styles.count}>{users.length}</span>
          </div>
          {otherUsers.map((u) => (
            <div key={u.name} className={styles.participantItem}>
              <Avatar name={u.name} avatarId={u.avatarId} size={34} />
              <div className={styles.participantInfo}>
                <span className={styles.participantName}>{u.name}</span>
                <span className={styles.participantStatus}>
                  <span className={styles.statusOnline} />
                  Online
                </span>
              </div>
            </div>
          ))}
          {otherUsers.length === 0 && (
            <div className={styles.noParticipants}>No other participants yet</div>
          )}
        </div>

        <div className={styles.sidebarFooter}>
          <div className={styles.connectionRow}>
            {connected ? <FiWifi size={14} /> : <FiWifiOff size={14} />}
            <span>{connected ? 'Connected' : 'Reconnecting...'}</span>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className={styles.sidebarOverlay}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className={styles.chatContainer}>
        <ChatHeader
          roomId={roomId}
          username={name}
          avatarId={myAvatarId}
          copied={copied}
          connected={connected}
          participantCount={users.length}
          onCopyLink={handleCopyLink}
          onLeave={handleLeaveRoom}
          onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        />

        <MessageList messages={messages} currentUser={name} />

        <MessageInput
          value={text}
          onChange={(e) => setText(e.target.value)}
          onSend={handleSendMessage}
        />
      </div>
    </div>
  );
}
